import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from "@mui/material";
import { type Result as ZXingResult } from "@zxing/library";
import {
  useCallback,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import BarcodeScanner from "react-qr-barcode-scanner";
import { development } from "../config";
import type { ErrorLike } from "../types";
import CameraSelector from "./CameraSelector";
import CloseButton from "./CloseButton";
import Dialog from "./dialog";

const BarcodeScannerComponent = BarcodeScanner as ComponentType<{
  onUpdate: (arg0: unknown, result?: ZXingResult) => void;
  onError?: (error: string | DOMException) => void;
  width?: number | string;
  height?: number | string;
  facingMode?: "environment" | "user";
  torch?: boolean;
  delay?: number;
  videoConstraints?: MediaTrackConstraints;
  stopStream?: boolean;
}>;

export type Props = {
  title: ReactNode;
  open: boolean;
  onClose(): void;
  onScan(code: string): void;
};

export default function BarcodeScannerDialog({
  title,
  open,
  onClose,
  onScan,
}: Props) {
  const [deviceId, setDeviceId] = useState<string | undefined>();
  const [error, setError] = useState<string | ErrorLike>();

  const onUpdate = useCallback(
    (_: any, result: any) => {
      const text = result?.text;
      if (text !== undefined && text !== "") onScan(text);
    },
    [onScan],
  );

  return (
    <Dialog title={title} open={open} onClose={onClose}>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === "string" ? error : error.message}
          </Alert>
        ) : (
          <>
            <BarcodeScannerComponent
              torch
              videoConstraints={{ deviceId }}
              height="auto"
              onUpdate={onUpdate}
              onError={setError}
            />
            {development && <BarcodeFake onUpdate={onUpdate} />}
          </>
        )}
        <CameraSelector onDeviceChange={setDeviceId} fullWidth sx={{ mt: 3 }} />
      </DialogContent>
      <DialogActions>
        <CloseButton intent="cancel" onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
}

function BarcodeFake({ onUpdate }: { onUpdate(_: any, result: any): void }) {
  const [value, setValue] = useState("");

  return (
    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
      <TextField
        label="Manuelle Eingabe"
        size="small"
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button
        variant="contained"
        color="secondary"
        type="button"
        onClick={() => onUpdate({}, { text: value })}
      >
        OK
      </Button>
    </Box>
  );
}
